import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatError, API } from "@/lib/api";
import { toast } from "@/lib/toast";
import { StaffContextCard } from "@/components/admin/StaffContextCard";
import { Hash, Download, Send, MessagesSquare, X, MessageSquare, Loader2, Users2, ImagePlus, Settings, Film } from "lucide-react";
import { timeAgo } from "@/lib/constants";
import { GroupManageModal } from "@/components/inbox/StaffCreateModals";

const MENTION_RE = /@[a-zA-Z0-9_]{2,30}/g;

// Render body text with @handles highlighted (the visual "you/someone was tagged" cue).
function renderWithMentions(text) {
  if (!text) return null;
  const out = [];
  let last = 0, m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <span key={m.index} className="text-olive-bright font-semibold" data-testid="staff-msg-mention">{m[0]}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function ChatMessage({ m, onOpenThread, onOpenImage }) {
  // Inline system line (membership change / rename) — system-style, not a chat bubble.
  if (m.system || m.kind === "system") {
    return (
      <div className="px-3 py-1.5 flex justify-center" data-testid={`staff-msg-system-${m.message_id}`}>
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground bg-white/5 border border-sniper/60 px-2.5 py-0.5 rounded-full">
          {m.text} <span className="opacity-60">· {timeAgo(m.created_at)}</span>
        </span>
      </div>
    );
  }
  return (
    <div className="px-3 py-2 hover:bg-white/5 transition-colors group" data-testid={`staff-msg-${m.message_id}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs text-olive-bright">@{m.sender_username}</span>
        <span className="font-mono text-[10px] text-muted-foreground">{timeAgo(m.created_at)}</span>
      </div>
      {m.text ? <p className="text-sm font-body text-foreground whitespace-pre-wrap break-words mt-0.5">{renderWithMentions(m.text)}</p> : null}
      {m.attachments?.length > 0 && (
        <div className={`grid gap-1.5 mt-1.5 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"} max-w-sm`} data-testid={`staff-msg-attachments-${m.message_id}`}>
          {m.attachments.map((a, i) => (
            <button key={i} type="button" onClick={() => onOpenImage?.(a.url)}
              className="block overflow-hidden border border-sniper bg-obsidian hover:opacity-90 transition-opacity"
              data-testid={`staff-msg-attachment-${m.message_id}-${i}`}>
              <img src={a.thumb_url || a.url} alt="Shared photo" loading="lazy" className="w-full h-full max-h-56 object-cover" />
            </button>
          ))}
        </div>
      )}
      {m.video && (
        <div className={`${m.text || m.attachments?.length ? "mt-1.5" : "mt-1"} max-w-sm`} data-testid={`staff-msg-video-${m.message_id}`}>
          <video src={m.video} controls playsInline preload="metadata" className="w-full max-h-64 rounded-sm border border-sniper bg-black" />
        </div>
      )}
      {(m.context_cards || []).map((c) => <StaffContextCard key={`${c.type}-${c.id}`} card={c} />)}
      {onOpenThread && (
        <button onClick={() => onOpenThread(m)} data-testid={`staff-thread-open-${m.message_id}`}
          className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase text-muted-foreground hover:text-olive-bright transition-colors">
          <MessagesSquare className="w-3 h-3" /> {m.reply_count > 0 ? `${m.reply_count} repl${m.reply_count === 1 ? "y" : "ies"}` : "Reply in thread"}
        </button>
      )}
    </div>
  );
}

const MAX_ATTACH = 6;
const MAX_VIDEO_MB = 50;

function Composer({ onSend, placeholder, testid, mentionCandidates = [] }) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [video, setVideo] = useState(null); // {url, duration}
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // string being typed after @, or null
  const fileRef = useRef();
  const videoRef = useRef();
  const inputRef = useRef();

  const pickFiles = () => fileRef.current?.click();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => (f.type || "").startsWith("image/") || /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(f.name)
    );
    if (e.target) e.target.value = "";
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATTACH) { toast.error(`Up to ${MAX_ATTACH} photos per message.`); return; }
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" is over 10MB.`); continue; }
        const fd = new FormData(); fd.append("file", f);
        try {
          const { data } = await api.post("/chat/upload-attachment", fd);
          setAttachments((prev) => [...prev, data]);
        } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Upload failed."); }
      }
    } finally { setUploading(false); }
  };
  const removeAttachment = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const pickVideo = () => videoRef.current?.click();
  const onVideo = async (e) => {
    const f = (e.target.files || [])[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!(f.type || "").startsWith("video/") && !/\.(mp4|mov|webm|m4v|mkv|3gp)$/i.test(f.name)) {
      toast.error("Choose a video file (MP4, MOV, WebM, M4V, MKV or 3GP)."); return;
    }
    if (f.size > MAX_VIDEO_MB * 1024 * 1024) { toast.error(`Video is over ${MAX_VIDEO_MB}MB.`); return; }
    setUploadingVideo(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/chat/upload-video", fd);
      setVideo(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Video upload failed."); }
    finally { setUploadingVideo(false); }
  };

  // @-mention autocomplete: detect an @token immediately before the caret.
  const syncMention = (el) => {
    const pos = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, pos);
    const mm = before.match(/(?:^|\s)@([a-zA-Z0-9_]{0,30})$/);
    setMentionQuery(mm ? mm[1].toLowerCase() : null);
  };
  const onTextChange = (e) => { setText(e.target.value); syncMention(e.target); };

  const suggestions = mentionQuery === null ? [] : mentionCandidates
    .filter((c) => {
      const u = (c.username || "").toLowerCase();
      const n = (c.name || "").toLowerCase();
      return !mentionQuery || u.includes(mentionQuery) || n.includes(mentionQuery);
    })
    .slice(0, 6);

  const applyMention = (username) => {
    const el = inputRef.current;
    const pos = el?.selectionStart ?? text.length;
    const before = text.slice(0, pos);
    const after = text.slice(pos);
    const replaced = before.replace(/(^|\s)@([a-zA-Z0-9_]{0,30})$/, `$1@${username} `);
    const next = replaced + after;
    setText(next);
    setMentionQuery(null);
    setTimeout(() => { if (el) { el.focus(); const c = replaced.length; el.setSelectionRange(c, c); } }, 0);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (uploading || uploadingVideo || busy) return;
    if (!text.trim() && attachments.length === 0 && !video) return;
    setBusy(true);
    try { await onSend(text, attachments, video?.url || null); setText(""); setAttachments([]); setVideo(null); setMentionQuery(null); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="p-2.5 border-t border-sniper relative">
      {suggestions.length > 0 && (
        <div className="absolute bottom-full left-2.5 right-2.5 mb-1 z-30 bg-obsidian border border-sniper shadow-lg max-h-48 overflow-y-auto" data-testid={`${testid}-mention-menu`}>
          {suggestions.map((c) => (
            <button key={c.username} type="button" onMouseDown={(ev) => { ev.preventDefault(); applyMention(c.username); }}
              data-testid={`${testid}-mention-${c.username}`}
              className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 transition-colors">
              <span className="font-mono text-xs text-olive-bright">@{c.username}</span>
              {c.name && <span className="font-body text-[11px] text-muted-foreground truncate">{c.name}</span>}
            </button>
          ))}
        </div>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testid}-staged`}>
          {attachments.map((a, i) => (
            <div key={i} className="relative w-14 h-14 border border-sniper overflow-hidden" data-testid={`${testid}-staged-${i}`}>
              <img src={a.thumb_url || a.url} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeAttachment(i)} data-testid={`${testid}-staged-remove-${i}`}
                title="Remove" className="absolute top-0 right-0 bg-black/70 hover:bg-black/90 text-white p-0.5 transition-colors"><X className="w-3 h-3" /></button>
            </div>
          ))}
          {uploading && <div className="w-14 h-14 border border-dashed border-sniper flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
        </div>
      )}
      {attachments.length === 0 && uploading && (
        <div className="mb-2 flex items-center gap-2 font-mono text-[10px] text-muted-foreground" data-testid={`${testid}-uploading`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading photo…</div>
      )}
      {video && (
        <div className="mb-2 relative inline-block" data-testid={`${testid}-staged-video`}>
          <video src={video.url} controls playsInline preload="metadata" className="max-h-28 rounded-sm border border-sniper bg-black" />
          <button type="button" onClick={() => setVideo(null)} data-testid={`${testid}-staged-video-remove`}
            title="Remove video" className="absolute top-0 right-0 bg-black/70 hover:bg-black/90 text-white p-0.5 transition-colors"><X className="w-3 h-3" /></button>
          {video.duration != null && <span className="absolute bottom-1 left-1 bg-black/70 text-white font-mono text-[9px] px-1 rounded-sm">{Math.round(video.duration)}s</span>}
        </div>
      )}
      {uploadingVideo && (
        <div className="mb-2 flex items-center gap-2 font-mono text-[10px] text-muted-foreground" data-testid={`${testid}-video-uploading`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading video…</div>
      )}
      <div className="flex gap-2">
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid={`${testid}-attach-input`} />
        <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={onVideo} data-testid={`${testid}-video-input`} />
        <button type="button" onClick={pickFiles} disabled={uploading || attachments.length >= MAX_ATTACH} data-testid={`${testid}-attach-btn`}
          title="Attach photos" className="px-3 flex items-center bg-input border border-sniper hover:border-olive text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ImagePlus className="w-4 h-4" /></button>
        <button type="button" onClick={pickVideo} disabled={uploadingVideo || !!video} data-testid={`${testid}-video-btn`}
          title="Attach a short video (max 60s)" className="px-3 flex items-center bg-input border border-sniper hover:border-olive text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Film className="w-4 h-4" /></button>
        <input ref={inputRef} value={text} onChange={onTextChange} onKeyUp={(e) => syncMention(e.currentTarget)} onClick={(e) => syncMention(e.currentTarget)} data-testid={testid}
          placeholder={placeholder} className="flex-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-sm text-foreground transition-colors" />
        <button type="submit" disabled={busy || uploading || uploadingVideo || (!text.trim() && attachments.length === 0 && !video)} data-testid={`${testid}-send`} className="bg-olive hover:bg-olive-bright text-white px-3 transition-colors disabled:opacity-50"><Send className="w-4 h-4" /></button>
      </div>
    </form>
  );
}

// INTERNAL (channels) + STAFF (staff DMs) + GROUP chat pane. Extracted from the retired /admin/team
// so the unified inbox hosts staff chat next to marketplace. Keeps channels, threads and export.
export default function StaffChatPane({ view, onActivity, onExitGroup }) {
  const [messages, setMessages] = useState([]);
  const [thread, setThread] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [groupInfo, setGroupInfo] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const bottomRef = useRef();
  const listRef = useRef();

  const loadMessages = useCallback(async (silent) => {
    if (!view) return;
    try {
      const url = view.kind === "channel"
        ? `/admin/team/channels/${view.id}/messages`
        : view.kind === "group"
        ? `/groups/${view.id}/messages`
        : `/admin/team/dms/${view.id}/messages`;
      const { data } = await api.get(url);
      setMessages(data.messages || []);
      if (view.kind === "group") setGroupInfo(data.group || null);
      onActivity?.();
      if (!silent) setTimeout(() => { const el = listRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, 60);
    } catch { /* keep prior */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view?.kind, view?.id]);

  // Mention autocomplete candidates: staff members for channels/DMs, group members for a group.
  const loadCandidates = useCallback(async () => {
    if (!view) { setMentionCandidates([]); return; }
    try {
      if (view.kind === "group") {
        const { data } = await api.get(`/groups/${view.id}`);
        setMentionCandidates((data.members || []).map((m) => ({ username: m.username, name: m.name })));
      } else {
        const { data } = await api.get("/admin/team/members");
        setMentionCandidates((data.members || []).map((m) => ({ username: m.username, name: m.name })));
      }
    } catch { setMentionCandidates([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view?.kind, view?.id]);

  const openThread = useCallback(async (rootId) => {
    try {
      const { data } = await api.get(`/admin/team/threads/${rootId}/messages`);
      setThread(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }, []);

  useEffect(() => { setThread(null); setGroupInfo(null); loadMessages(false); loadCandidates(); }, [loadMessages, loadCandidates]);
  useEffect(() => {
    const t = setInterval(() => loadMessages(true), 4000);
    return () => clearInterval(t);
  }, [loadMessages]);
  useEffect(() => {
    if (!thread?.root?.message_id) return;
    const t = setInterval(() => openThread(thread.root.message_id), 5000);
    return () => clearInterval(t);
  }, [thread?.root?.message_id, openThread]);

  const sendMessage = async (text, attachments = [], video = null) => {
    try {
      const payload = { text, attachments, video };
      if (view.kind === "channel") await api.post(`/admin/team/channels/${view.id}/messages`, payload);
      else if (view.kind === "group") await api.post(`/groups/${view.id}/messages`, payload);
      else await api.post(`/admin/team/dms/${view.id}/messages`, payload);
      loadMessages(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const sendThread = async (text, attachments = [], video = null) => {
    try {
      await api.post(`/admin/team/threads/${thread.root.message_id}/messages`, { text, attachments, video });
      openThread(thread.root.message_id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const exportChat = async (format) => {
    try {
      const channel = view?.kind === "channel" ? view.id : "";
      const token = localStorage.getItem("os_token");
      const res = await fetch(`${API}/admin/team/export?format=${format}${channel ? `&channel=${channel}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `staff_chat_${channel || "all"}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Export downloaded.");
    } catch { toast.error("Export failed."); }
  };

  if (!view) return <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono uppercase text-sm">Select a channel, group or DM</div>;

  const HeadIcon = view.kind === "channel" ? Hash : view.kind === "group" ? Users2 : MessageSquare;
  const headTitle = view.kind === "group" ? (groupInfo?.title || view.name) : view.name;
  const headSub = view.kind === "dm" && view.username
    ? `@${view.username}`
    : view.kind === "group"
    ? `${groupInfo?.member_count ?? ""} ${(groupInfo?.member_count === 1) ? "member" : "members"}`.trim()
    : view.topic;
  const composerPlaceholder = view.kind === "channel"
    ? `Message #${view.name}`
    : view.kind === "group"
    ? `Message ${headTitle}`
    : `Message ${view.name}`;

  return (
    <div className="flex h-full min-w-0">
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <div className="px-4 py-3 border-b border-sniper flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2">
              <HeadIcon className="w-4 h-4 text-olive-bright" />
              {headTitle}
            </h3>
            {headSub && <p className="font-mono text-[10px] uppercase text-muted-foreground truncate">{headSub}</p>}
          </div>
          {view.kind === "channel" && (
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => exportChat("json")} data-testid="staff-export-json" title="Export JSON" className="border border-sniper hover:border-olive text-muted-foreground hover:text-foreground font-mono uppercase text-[10px] px-2 py-1.5 transition-colors flex items-center gap-1"><Download className="w-3 h-3" /> JSON</button>
              <button onClick={() => exportChat("csv")} data-testid="staff-export-csv" title="Export CSV" className="border border-sniper hover:border-olive text-muted-foreground hover:text-foreground font-mono uppercase text-[10px] px-2 py-1.5 transition-colors flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
            </div>
          )}
          {view.kind === "group" && (
            <button onClick={() => setManageOpen(true)} data-testid="group-manage-btn" title="Manage group"
              className="shrink-0 border border-sniper hover:border-olive text-muted-foreground hover:text-foreground font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors flex items-center gap-1.5">
              <Settings className="w-3.5 h-3.5" /> Manage
            </button>
          )}
        </div>
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="staff-messages">
          {messages.length === 0 && <p className="p-6 text-sm font-mono text-muted-foreground">No messages yet. Start the conversation.</p>}
          {messages.map((m) => <ChatMessage key={m.message_id} m={m} onOpenImage={setLightbox} onOpenThread={view.kind === "channel" ? (msg) => openThread(msg.message_id) : null} />)}
          <div ref={bottomRef} />
        </div>
        <Composer onSend={sendMessage} placeholder={composerPlaceholder} testid="staff-composer" mentionCandidates={mentionCandidates} />
      </div>

      {thread && (
        <aside className="w-[320px] shrink-0 border-l border-sniper bg-surface flex flex-col" data-testid="staff-thread-panel">
          <div className="px-4 py-3 border-b border-sniper flex items-center justify-between">
            <span className="font-heading uppercase text-foreground flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-olive-bright" /> Thread</span>
            <button onClick={() => setThread(null)} data-testid="staff-thread-close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="border-b border-sniper bg-obsidian/40"><ChatMessage m={thread.root} onOpenImage={setLightbox} /></div>
            {thread.messages.map((m) => <ChatMessage key={m.message_id} m={m} onOpenImage={setLightbox} />)}
          </div>
          <Composer onSend={sendThread} placeholder="Reply in thread…" testid="staff-thread-composer" mentionCandidates={mentionCandidates} />
        </aside>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-4" onClick={() => setLightbox(null)} data-testid="staff-image-lightbox">
          <button onClick={() => setLightbox(null)} data-testid="staff-image-lightbox-close" className="absolute top-16 right-4 z-[201] text-white/80 hover:text-white"><X className="w-6 h-6" /></button>
          <img src={lightbox} alt="Shared photo" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {manageOpen && view.kind === "group" && (
        <GroupManageModal
          gid={view.id}
          onClose={() => setManageOpen(false)}
          onChanged={() => { loadMessages(false); loadCandidates(); onActivity?.(); }}
          onLeft={() => { setManageOpen(false); onExitGroup?.(); }}
        />
      )}
    </div>
  );
}
